import React, { useState } from 'react';
import { Crown, Eye, EyeOff, Send } from 'lucide-react';
import { toast } from 'sonner';
import { useRest, restFetch, RCard } from './restui';

/* Ready-to-send VIP onboarding email (welcome link + access + Pixel snippet).
   The AI prepares it; nothing is sent until a human clicks Send. Owner/admin only. */
export function VipInviteCard({ isEs }) {
  const { data: d } = useRest(`/vip-invite?lang=${isEs ? 'es' : 'en'}`);
  const [to, setTo] = useState('');
  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  if (!d?.ok) return null;
  const send = async () => {
    if (!to.trim()) { toast.error(isEs ? 'Escribe el email del destinatario' : 'Enter the recipient email'); return; }
    setSending(true);
    try {
      const r = await restFetch('/vip-invite/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ to: to.trim(), lang: isEs ? 'es' : 'en' }),
      });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.ok) toast.success(isEs ? `Invitación enviada a ${to.trim()}` : `Invitation sent to ${to.trim()}`);
      else toast.error(isEs ? 'No se pudo enviar — revisa el email o el servicio de correo' : 'Could not send — check the email or the mail service');
    } catch {
      toast.error(isEs ? 'Error al enviar' : 'Send failed');
    }
    setSending(false);
  };
  return (
    <RCard className="p-6 mb-5 border-amber-200" testid="vip-invite-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-amber-600">
            <Crown className="w-3.5 h-3.5" /> {isEs ? 'Email de invitación VIP' : 'VIP invitation email'}
          </p>
          <p className="mt-1 text-[14px] font-semibold text-gray-900" data-testid="vip-invite-subject">{d.subject}</p>
          <p className="mt-1 text-[12.5px] text-gray-500 max-w-xl">
            {isEs
              ? 'Listo para enviar: bienvenida personalizada, accesos al Restaurant AI OS y el snippet del Pixel para su web. Nada se envía sin que tú pulses Enviar.'
              : 'Ready to send: personalized welcome, Restaurant AI OS access and the Pixel snippet for their website. Nothing is sent until you click Send.'}
          </p>
        </div>
        <button onClick={() => setOpen(!open)} data-testid="vip-invite-preview-toggle"
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 px-4 py-2 text-[12px] font-bold text-gray-700 hover:border-gray-500 transition-colors shrink-0">
          {open ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {open ? (isEs ? 'Ocultar vista previa' : 'Hide preview') : (isEs ? 'Ver vista previa' : 'Preview email')}
        </button>
      </div>
      {open && (
        <iframe title="vip-invite-preview" srcDoc={d.html} sandbox="" data-testid="vip-invite-preview"
          className="mt-4 w-full h-[430px] rounded-xl border border-gray-200 bg-white" />
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <input type="email" value={to} onChange={(e) => setTo(e.target.value)} data-testid="vip-invite-to"
          placeholder={isEs ? 'email del destinatario (ej. info@kitxens.com)' : 'recipient email (e.g. info@kitxens.com)'}
          className="flex-1 min-w-[220px] rounded-xl border border-gray-200 px-3.5 py-2.5 text-[13px] focus:outline-none focus:border-[var(--rest-accent)]" />
        <button onClick={send} disabled={sending} data-testid="vip-invite-send"
          className="inline-flex items-center gap-1.5 rounded-full bg-[var(--rest-accent)] text-white px-5 py-2.5 text-[12.5px] font-bold hover:opacity-90 transition-opacity disabled:opacity-50">
          <Send className="w-3.5 h-3.5" /> {sending ? (isEs ? 'Enviando…' : 'Sending…') : (isEs ? 'Enviar invitación' : 'Send invitation')}
        </button>
      </div>
    </RCard>
  );
}
